import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {NGXLogger} from 'ngx-logger';

import {AuthService, CookieProbeScope} from './auth.service';
import {FastenService} from './fasten.service';

@Injectable({providedIn: 'root'})
export class VaultProfileSigninFlowService {
  readonly needStorageAccessPermissionSubject = new BehaviorSubject(false);
  readonly userInteractionCompletedSubject = new BehaviorSubject(false);

  private userInteractionWindowOpened = false;
  private welcomeCompleted = false;

  constructor(
    private authService: AuthService,
    private fastenService: FastenService,
    private logger: NGXLogger,
  ) {}

  initializeStorageAccess(): void {
    if (!this.requiresStorageAccess()) {
      this.logger.log('Storage Access API fallback is not required or is unavailable.');
      this.needStorageAccessPermissionSubject.next(false);
      return;
    }

    this.needStorageAccessPermissionSubject.next(true);
    this.hasStorageAccess().then((hasAccess) => {
      this.needStorageAccessPermissionSubject.next(!hasAccess);
    });
  }

  hasCompletedWelcome(): boolean {
    return this.welcomeCompleted;
  }

  completeWelcome(): void {
    this.welcomeCompleted = true;
  }

  requiresStorageAccess(): boolean {
    return this.authService.RequiresStorageAccessFallback();
  }

  isStorageAccessApiSupportedByBrowser(): boolean {
    return typeof document.hasStorageAccess === 'function'
      && typeof document.requestStorageAccess === 'function';
  }

  hasStorageAccess(): Promise<boolean> {
    if (!this.isStorageAccessApiSupportedByBrowser()) {
      this.logger.warn('Storage Access API not available in this browser.');
      return Promise.resolve(true);
    }

    return document.hasStorageAccess()
      .then((hasAccess) => {
        this.logger.log('Storage Access API unpartitioned or already granted!', hasAccess);
        return hasAccess;
      })
      .catch((error) => {
        this.logger.error('Storage access has not been granted', error);
        return false;
      });
  }

  requestStorageAccess(): Promise<boolean> {
    if (!this.isStorageAccessApiSupportedByBrowser()) {
      this.logger.warn('Storage Access API not available in this browser.');
      return Promise.resolve(true);
    }

    return document.requestStorageAccess()
      .then(async () => {
        this.logger.log('Storage access granted!');
        const cookieSupported = await this.authService.CheckCookieSupport(CookieProbeScope.Regular);
        if (!cookieSupported) {
          const error = new Error('Storage access was granted, but cookies are still blocked. Please allow cookies and try again.');
          this.restoreFirstPartyConsent();
          this.logger.error('Storage access cookie verification failed', error);
          throw error;
        }

        return true;
      }, (error: unknown) => {
        this.restoreFirstPartyConsent();
        this.logger.log('Storage access denied by user', error);
        alert('Cookies are required for the Fasten widget to function. Please allow storage access to continue.');
        return Promise.reject(error);
      });
  }

  awaitUserInteractionCompleted(): void {
    if (!this.needStorageAccessPermissionSubject.getValue()) {
      this.logger.log('No storage access required, no user interaction needed.');
      return;
    }
    if (this.userInteractionCompletedSubject.getValue()) {
      this.logger.log('User interaction already completed, no need to wait.');
      return;
    }
    if (this.userInteractionWindowOpened) {
      this.logger.log('User interaction window already opened, waiting for completion.');
      return;
    }

    this.userInteractionWindowOpened = true;
    this.fastenService.storageApiUserInteractionWithPopup().subscribe((result) => {
      this.logger.log('User interaction completed', result);
      this.userInteractionCompletedSubject.next(result.success);
      this.userInteractionWindowOpened = false;
    });
  }

  private restoreFirstPartyConsent(): void {
    this.needStorageAccessPermissionSubject.next(true);
    this.userInteractionCompletedSubject.next(false);
    this.userInteractionWindowOpened = false;
  }
}
